import * as Sentry from "@sentry/react";
import { browserTracingIntegration } from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

// DSN comes exclusively from the VITE_SENTRY_DSN build env var. There is
// deliberately NO hardcoded fallback — a baked-in DSN would silently ship users'
// errors/PII to a project nobody controls. When unset, Sentry is disabled.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (!SENTRY_DSN) {
  console.warn(
    "[Sentry] VITE_SENTRY_DSN is not configured — frontend error reporting is disabled.",
  );
}

const PII_KEYS = new Set([
  "accountNumber",
  "fica",
  "password",
  "secret",
  "idNumber",
  "signatureDataUrl",
  "token",
]);

function scrubPii(obj: unknown): unknown {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(scrubPii);
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    result[key] = PII_KEYS.has(key) ? "[SCRUBBED_PII]" : scrubPii(value);
  }
  return result;
}

Sentry.init({
  dsn: SENTRY_DSN,
  enabled: Boolean(SENTRY_DSN),
  integrations: [browserTracingIntegration()],
  tracesSampler(samplingContext) {
    const name: string = samplingContext.name ?? "";
    if (
      /\/(payment-runs|wallet|bank-changes|transactions|onboarding|training)/.test(
        name,
      )
    ) {
      return 1.0;
    }
    return 0.1;
  },
  beforeSend(event) {
    if (event.request?.data && typeof event.request.data === "object") {
      event.request.data = scrubPii(event.request.data) as Record<
        string,
        unknown
      >;
    }
    if (event.extra) {
      event.extra = scrubPii(event.extra) as Record<string, unknown>;
    }
    return event;
  },
});

createRoot(document.getElementById("root")!).render(<App />);
